// ============================================
// FIXED: DaycarePrintReportModal.js
// Better spacing, alignment, and print layout
// ============================================

import React, { useRef } from 'react';
import { X, Printer } from 'lucide-react';

const DaycarePrintReportModal = ({ monthlyReport, selectedYear, onClose }) => {
  const printRef = useRef(null);

  // Calculate totals
  const totalRevenue = monthlyReport.reduce((sum, m) => sum + (m.TotalRevenue || 0), 0);
  const totalDiscounts = monthlyReport.reduce((sum, m) => sum + (m.TotalDiscounts || 0), 0);
  const totalNet = monthlyReport.reduce((sum, m) => sum + (m.NetRevenue || 0), 0);
  const totalCollected = monthlyReport.reduce((sum, m) => sum + (m.TotalPaymentsCollected || 0), 0);
  const totalOutstanding = monthlyReport.reduce((sum, m) => sum + (m.OutstandingBalance || 0), 0);
  const totalOldBalance = monthlyReport.reduce((sum, m) => sum + (m.OldBalance || 0), 0);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Daycare Monthly Report - ${selectedYear}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 15mm;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              line-height: 1.4;
              color: #1f2937;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            .report-container {
              width: 100%;
              padding: 8mm;
            }
            
            /* Header */
            .report-header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 12px;
              border-bottom: 3px solid #2563eb;
            }
            
            .school-name {
              font-size: 20px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 4px;
            }
            
            .report-title {
              font-size: 16px;
              color: #4b5563;
              margin-bottom: 2px;
            }
            
            .report-period {
              font-size: 12px;
              color: #6b7280;
              font-weight: 600;
            }
            
            .report-date {
              font-size: 9px;
              color: #9ca3af;
              margin-top: 4px;
            }
            
            /* Summary Section */
            .summary-section {
              margin-bottom: 15px;
              page-break-inside: avoid;
            }
            
            .summary-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
            }
            
            .summary-item {
              flex: 1;
              padding: 8px 10px;
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              margin: 0 4px;
              text-align: center;
            }
            
            .summary-item:first-child {
              margin-left: 0;
            }
            
            .summary-item:last-child {
              margin-right: 0;
            }
            
            .summary-label {
              font-size: 8px;
              text-transform: uppercase;
              color: #6b7280;
              font-weight: 600;
              display: block;
              margin-bottom: 3px;
            }
            
            .summary-amount {
              font-size: 14px;
              font-weight: bold;
              display: block;
            }
            
            .summary-item.revenue .summary-amount { color: #3b82f6; }
            .summary-item.discounts .summary-amount { color: #f97316; }
            .summary-item.net .summary-amount { color: #10b981; }
            .summary-item.collected .summary-amount { color: #8b5cf6; }
            .summary-item.outstanding .summary-amount { color: #ef4444; }
            .summary-item.old-balance .summary-amount { color: #6b7280; }
            
            /* Table */
            .report-table {
              width: 100%;
              border-collapse: collapse;
              background: white;
              page-break-inside: auto;
            }
            
            .report-table thead {
              background: #f3f4f6;
            }
            
            .report-table th {
              padding: 8px 6px;
              text-align: left;
              font-size: 8px;
              font-weight: 700;
              text-transform: uppercase;
              color: #374151;
              border: 1px solid #d1d5db;
              white-space: nowrap;
            }
            
            .report-table th.center { text-align: center; }
            .report-table th.right { text-align: right; }
            
            .report-table td {
              padding: 6px;
              border: 1px solid #e5e7eb;
              font-size: 9px;
              white-space: nowrap;
            }
            
            .report-table td.center { text-align: center; }
            .report-table td.right { text-align: right; }
            
            .report-table tbody tr.month-row {
              page-break-inside: avoid;
            }
            
            .report-table tfoot {
              background: #f3f4f6;
              font-weight: bold;
            }
            
            .report-table tfoot td {
              padding: 10px 6px;
              border: 1px solid #9ca3af;
              font-size: 10px;
            }
            
            /* Status column */
            .status-cell {
              font-size: 8px;
              line-height: 1.3;
            }
            
            .status-line {
              display: block;
              margin: 1px 0;
            }
            
            .status-icon {
              font-weight: bold;
              margin-right: 2px;
            }
            
            .status-paid { color: #065f46; }
            .status-partial { color: #92400e; }
            .status-pending { color: #1e40af; }
            .status-cancelled { color: #991b1b; }
            .status-frozen { color: #3730a3; }
            
            /* Footer */
            .report-footer {
              margin-top: 15px;
              padding-top: 10px;
              border-top: 2px solid #e5e7eb;
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              color: #6b7280;
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <!-- Header -->
            <div class="report-header">
              <div class="school-name">Collège des Apôtres</div>
              <div class="report-title">Daycare Monthly Financial Report</div>
              <div class="report-period">Academic Year: ${selectedYear}</div>
              <div class="report-date">Generated: ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</div>
            </div>
            
            <!-- Summary Section -->
            <div class="summary-section">
              <div class="summary-row">
                <div class="summary-item revenue">
                  <span class="summary-label">Total Revenue</span>
                  <span class="summary-amount">$${totalRevenue.toFixed(2)}</span>
                </div>
                <div class="summary-item discounts">
                  <span class="summary-label">Total Discounts</span>
                  <span class="summary-amount">$${totalDiscounts.toFixed(2)}</span>
                </div>
                <div class="summary-item net">
                  <span class="summary-label">Net Revenue</span>
                  <span class="summary-amount">$${totalNet.toFixed(2)}</span>
                </div>
              </div>
              <div class="summary-row">
                <div class="summary-item old-balance">
                  <span class="summary-label">Old Balance</span>
                  <span class="summary-amount">$${totalOldBalance.toFixed(2)}</span>
                </div>
                <div class="summary-item collected">
                  <span class="summary-label">Collected</span>
                  <span class="summary-amount">$${totalCollected.toFixed(2)}</span>
                </div>
                <div class="summary-item outstanding">
                  <span class="summary-label">Outstanding</span>
                  <span class="summary-amount">$${totalOutstanding.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <!-- Monthly Breakdown Table -->
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 12%;">Month</th>
                  <th class="center" style="width: 7%;">Enroll</th>
                  <th class="center" style="width: 7%;">Families</th>
                  <th class="center" style="width: 7%;">Children</th>
                  <th class="right" style="width: 10%;">Revenue</th>
                  <th class="right" style="width: 10%;">Discounts</th>
                  <th class="right" style="width: 10%;">Net</th>
                  <th class="right" style="width: 9%;">Old Balance</th>
                  <th class="right" style="width: 10%;">Collected</th>
                  <th class="right" style="width: 10%;">Outstanding</th>
                  <th class="center" style="width: 8%;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${monthlyReport.map(month => `
                  <tr class="month-row">
                    <td><strong>${month.MonthName} ${month.ChargeYear}</strong></td>
                    <td class="center">${month.ActiveEnrollmentsCount || 0}</td>
                    <td class="center">${month.TotalFamilies || 0}</td>
                    <td class="center">${month.TotalChildren || 0}</td>
                    <td class="right">$${(month.TotalRevenue || 0).toFixed(2)}</td>
                    <td class="right" style="color: #f97316;">$${(month.TotalDiscounts || 0).toFixed(2)}</td>
                    <td class="right" style="color: #10b981; font-weight: 600;">$${(month.NetRevenue || 0).toFixed(2)}</td>
                    <td class="right" style="color: #6b7280;">$${(month.OldBalance || 0).toFixed(2)}</td>
                    <td class="right" style="color: #8b5cf6;">$${(month.TotalPaymentsCollected || 0).toFixed(2)}</td>
                    <td class="right" style="color: ${(month.OutstandingBalance || 0) > 0 ? '#ef4444' : '#10b981'}; font-weight: 600;">
                      $${(month.OutstandingBalance || 0).toFixed(2)}
                    </td>
                    <td class="center status-cell">
                      ${month.PaidCount > 0 ? `<span class="status-line status-paid"><span class="status-icon">✓</span>${month.PaidCount} Paid</span>` : ''}
                      ${month.PartialCount > 0 ? `<span class="status-line status-partial"><span class="status-icon">⚡</span>${month.PartialCount} Partial</span>` : ''}
                      ${month.BilledCount > 0 ? `<span class="status-line status-pending"><span class="status-icon">●</span>${month.BilledCount} Pending</span>` : ''}
                      ${month.CancelledCount > 0 ? `<span class="status-line status-cancelled"><span class="status-icon">✗</span>${month.CancelledCount} Cancelled</span>` : ''}
                      ${(month.FrozenCount || 0) > 0 ? `<span class="status-line status-frozen"><span class="status-icon">❄</span>${month.FrozenCount} Frozen</span>` : ''}
                      ${!month.PaidCount && !month.PartialCount && !month.BilledCount && !month.CancelledCount && !month.FrozenCount ? '-' : ''}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>TOTAL</strong></td>
                  <td class="center">-</td>
                  <td class="center">-</td>
                  <td class="center">-</td>
                  <td class="right">$${totalRevenue.toFixed(2)}</td>
                  <td class="right" style="color: #f97316;">$${totalDiscounts.toFixed(2)}</td>
                  <td class="right" style="color: #10b981;">$${totalNet.toFixed(2)}</td>
                  <td class="right" style="color: #6b7280;">$${totalOldBalance.toFixed(2)}</td>
                  <td class="right" style="color: #8b5cf6;">$${totalCollected.toFixed(2)}</td>
                  <td class="right" style="color: #ef4444;">$${totalOutstanding.toFixed(2)}</td>
                  <td class="center">-</td>
                </tr>
              </tfoot>
            </table>
            
            <!-- Footer -->
            <div class="report-footer">
              <div>
                <strong>Collège des Apôtres - Daycare Department</strong><br>
                Jounieh, Lebanon
              </div>
              <div style="text-align: right;">
                <strong>Financial Summary</strong><br>
                Net Revenue: $${totalNet.toFixed(2)} | Outstanding: $${totalOutstanding.toFixed(2)}
              </div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 100);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Printer className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Print Preview</h2>
              <p className="text-sm text-blue-100">Daycare Monthly Report - {selectedYear}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Preview Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          <div 
            ref={printRef}
            className="bg-white shadow-lg mx-auto p-8"
            style={{ width: '1000px' }}
          >
            {/* Report Header */}
            <div className="text-center mb-6 pb-4 border-b-4 border-blue-600">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Collège des Apôtres</h1>
              <h2 className="text-xl text-gray-600 mb-1">Daycare Monthly Financial Report</h2>
              <p className="text-base font-semibold text-gray-700">Academic Year: {selectedYear}</p>
              <p className="text-xs text-gray-500 mt-2">
                Generated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-blue-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Total Revenue</div>
                <div className="text-xl font-bold text-blue-600">${totalRevenue.toFixed(2)}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-orange-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Total Discounts</div>
                <div className="text-xl font-bold text-orange-600">${totalDiscounts.toFixed(2)}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-green-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Net Revenue</div>
                <div className="text-xl font-bold text-green-600">${totalNet.toFixed(2)}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-gray-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Old Balance</div>
                <div className="text-xl font-bold text-gray-600">${totalOldBalance.toFixed(2)}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-purple-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Collected</div>
                <div className="text-xl font-bold text-purple-600">${totalCollected.toFixed(2)}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 border-l-4 border-l-red-500 rounded-lg p-3 text-center">
                <div className="text-xs uppercase text-gray-600 font-semibold mb-1">Outstanding</div>
                <div className="text-xl font-bold text-red-600">${totalOutstanding.toFixed(2)}</div>
              </div>
            </div>

            {/* Monthly Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse shadow-sm text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left font-bold text-gray-600 uppercase border border-gray-300">Month</th>
                    <th className="px-2 py-2 text-center font-bold text-gray-600 uppercase border border-gray-300">Enroll</th>
                    <th className="px-2 py-2 text-center font-bold text-gray-600 uppercase border border-gray-300">Fam</th>
                    <th className="px-2 py-2 text-center font-bold text-gray-600 uppercase border border-gray-300">Child</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Revenue</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Disc</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Net</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Old Bal</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Collected</th>
                    <th className="px-2 py-2 text-right font-bold text-gray-600 uppercase border border-gray-300">Outstanding</th>
                    <th className="px-2 py-2 text-center font-bold text-gray-600 uppercase border border-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {monthlyReport.map((month, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="px-2 py-2 font-semibold border border-gray-200">{month.MonthName} {month.ChargeYear}</td>
                      <td className="px-2 py-2 text-center border border-gray-200">{month.ActiveEnrollmentsCount || 0}</td>
                      <td className="px-2 py-2 text-center border border-gray-200">{month.TotalFamilies || 0}</td>
                      <td className="px-2 py-2 text-center border border-gray-200">{month.TotalChildren || 0}</td>
                      <td className="px-2 py-2 text-right border border-gray-200">${(month.TotalRevenue || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right text-orange-600 border border-gray-200">${(month.TotalDiscounts || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right font-semibold text-green-600 border border-gray-200">${(month.NetRevenue || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right text-gray-600 border border-gray-200">${(month.OldBalance || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right text-purple-600 border border-gray-200">${(month.TotalPaymentsCollected || 0).toFixed(2)}</td>
                      <td className={`px-2 py-2 text-right font-semibold border border-gray-200 ${
                        (month.OutstandingBalance || 0) > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        ${(month.OutstandingBalance || 0).toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-center border border-gray-200">
                        <div className="flex flex-col text-xs leading-tight">
                          {month.PaidCount > 0 && <div className="text-green-700">✓ {month.PaidCount}</div>}
                          {month.PartialCount > 0 && <div className="text-yellow-700">⚡ {month.PartialCount}</div>}
                          {month.BilledCount > 0 && <div className="text-blue-700">● {month.BilledCount}</div>}
                          {month.CancelledCount > 0 && <div className="text-red-700">✗ {month.CancelledCount}</div>}
                          {(month.FrozenCount || 0) > 0 && <div className="text-indigo-700">❄ {month.FrozenCount}</div>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-bold">
                  <tr className="border-t-2 border-gray-400">
                    <td className="px-2 py-3 border border-gray-300">TOTAL</td>
                    <td className="px-2 py-3 text-center border border-gray-300">-</td>
                    <td className="px-2 py-3 text-center border border-gray-300">-</td>
                    <td className="px-2 py-3 text-center border border-gray-300">-</td>
                    <td className="px-2 py-3 text-right border border-gray-300">${totalRevenue.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right text-orange-600 border border-gray-300">${totalDiscounts.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right text-green-600 border border-gray-300">${totalNet.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right text-gray-600 border border-gray-300">${totalOldBalance.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right text-purple-600 border border-gray-300">${totalCollected.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right text-red-600 border border-gray-300">${totalOutstanding.toFixed(2)}</td>
                    <td className="px-2 py-3 text-center border border-gray-300">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t-2 border-gray-300 flex justify-between text-xs text-gray-600">
              <div>
                <strong className="block text-sm">Collège des Apôtres - Daycare Department</strong>
                <span>Jounieh, Lebanon</span>
              </div>
              <div className="text-right">
                <strong className="block text-sm">Financial Summary</strong>
                <span>Net Revenue: ${totalNet.toFixed(2)} | Outstanding: ${totalOutstanding.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer - Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">{monthlyReport.length}</span> month(s) • 
            <span className="font-semibold ml-1">${totalOutstanding.toFixed(2)}</span> outstanding
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              <Printer className="w-4 h-4" />
              <span>Print Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DaycarePrintReportModal;